<template>
  <div class="bot-onboarding d-flex flex-column align-items-center justify-content-center p-4">
    <transition name="fade">
      <div
        :key="step"
        class="step-wrapper text-center w-100"
      >
        <p class="bot-question mb-2">
          <span
            v-if="isThinking"
            class="thinking-dots"
          >...</span>
          <span v-else>{{ typedText }}</span>
        </p>
        <p
          v-if="!isThinking && steps[step - 1].exemplo"
          class="bot-exemplo mb-4 text-muted"
        >
          Exemplo: {{ steps[step - 1].exemplo }}
        </p>

        <!-- Escolha de linguagem -->
        <div
          v-if="isLanguageStyleStep"
          class="language-cards-wrapper"
        >
          <div
            v-for="option in languageOptions"
            :key="option.value"
            class="language-card"
            :class="{ selected: form.languageStyle === option.value }"
            @click="selectLanguageStyle(option.value)"
          >
            <h5 class="mb-1">
              {{ option.title }}
            </h5>
            <small class="text-muted">{{ option.description }}</small>
          </div>
        </div>

        <!-- Escolha de loja -->
        <div
          v-else-if="isStoreStep"
          class="language-cards-wrapper"
        >
          <div
            v-for="store in storeOptions"
            :key="store.id"
            class="language-card"
            :class="{ selected: form.storeId === store.id }"
            @click="selectStore(store.id)"
          >
            <h5 class="mb-0">
              {{ store.name }}
            </h5>
          </div>
        </div>

        <div
          v-else
          class="input-wrapper"
        >
          <textarea
            v-model="form[steps[step - 1].model]"
            class="answer-input"
            :maxlength="maxChars"
            rows="3"
            placeholder="Digite sua resposta..."
            :disabled="!showInput"
            @input="updateCharCount"
          />
          <div class="char-counter">
            {{ charCount }} / {{ maxChars }}
          </div>
        </div>

        <div class="navigation-buttons d-flex justify-content-between w-100 mt-4">
          <b-button
            variant="outline-secondary"
            :disabled="step === 1 || isThinking"
            @click="prevStep"
          >
            Voltar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!isCurrentFieldValid || isThinking"
            @click="nextStep"
          >
            Próximo
          </b-button>
        </div>
      </div>
    </transition>
    <div
      v-if="isLoadingFinalStep"
      class="loading-overlay d-flex align-items-center justify-content-center"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="sr-only">Carregando...</span>
      </div>
      <span class="ml-2">Configurando seu assistente...</span>
    </div>

  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import stores from '../../../functions/basics/stores'
import { saveKnowledge, createBot, editRoleBot } from '../../../functions/basics/bot'

export default {
  name: 'BotConfiguration',
  data() {
    return {
      isLoadingFinalStep: false,
      typingInterval: null,
      step: 1,
      maxChars: 250,
      charCount: 0,
      typedText: '',
      showInput: false,
      isThinking: true,
      typingSpeed: 30,
      form: {
        botName: '',
        languageStyle: '',
        storeId: null,
        deliveryDelay: '',
        exchangePolicy: '',
        cancelPolicy: '',
        additionalInfo: '',
      },
      steps: [
        { question: 'Como devo me chamar?', exemplo: '', model: 'botName' },
        { question: 'Qual o estilo de linguagem que devo usar?', exemplo: '', model: 'languageStyle' },
        { question: 'Qual loja eu devo atender?', exemplo: '', model: 'storeId' },
        { question: 'O que preciso saber sobre os atrasos na entrega?', exemplo: 'Pode haver atrasos em finais de semanas e datas comemorativas.', model: 'deliveryDelay' },
        { question: 'Qual a política de troca da loja?', exemplo: 'Trocas somente de produtos com avarias e no durante o período de 7 dias após o recebimento.', model: 'exchangePolicy' },
        { question: 'Qual as regras para cancelar um pedido?', exemplo: '', model: 'cancelPolicy' },
        { question: 'Me conte quais as promoções ou cupons ativos no seu site.', exemplo: 'Cupom ANIVER10 com 10% de desconto para todos os pedidos.', model: 'additionalInfo' },
      ],
      languageOptions: [
        { value: 'neutro', title: 'Neutro', description: 'Respostas diretas, objetivas' },
        { value: 'divertido', title: 'Divertido', description: 'Respostas leves e descontraídas' },
      ],
      storeOptions: [],
    }
  },
  computed: {
    isCurrentFieldValid() {
      const { model } = this.steps[this.step - 1]
      const value = this.form[model]
      return value && value.toString().trim().length > 0
    },
    isLanguageStyleStep() {
      return this.steps[this.step - 1].model === 'languageStyle'
    },
    isStoreStep() {
      return this.steps[this.step - 1].model === 'storeId'
    },
  },
  async created() {
    await this.carregarLojas()
    this.initStep()
  },
  mounted() {
    this.initStep()
  },
  beforeDestroy() {
    if (this.typingInterval) {
      clearInterval(this.typingInterval)
    }
  },
  methods: {
    nextStep() {
      const isLastStep = this.step === this.steps.length

      if (isLastStep) {
        this.isLoadingFinalStep = true

        // Opcional: delay para mostrar o loading por pelo menos 1s
        setTimeout(() => {
          this.finalizarConfig()
        }, 1000)
      } else {
        this.step++
        this.initStep()
      }
    },
    selectStore(storeId) {
      this.form.storeId = storeId
      this.charCount = String(storeId).length
    },
    async carregarLojas() {
      const lojas = await stores()
      this.storeOptions = lojas.map(loja => ({
        id: loja.id,
        name: loja.name,
      }))
    },
    selectLanguageStyle(value) {
      this.form.languageStyle = value
      this.charCount = value.length
    },
    updateCharCount() {
      const { model } = this.steps[this.step - 1]
      this.charCount = this.form[model]?.length || 0
    },
    prevStep() {
      if (this.step > 1) {
        this.step--
        this.initStep()
      }
    },
    initStep() {
      this.typedText = ''
      this.showInput = false
      this.isThinking = true
      this.charCount = 0

      // simula o "pensando..." por 2 segundos
      setTimeout(() => {
        this.isThinking = false
        this.startTyping()
      }, 2000)
    },
    startTyping() {
      const fullText = this.steps[this.step - 1].question
      let i = 0

      // Previne múltiplos intervals simultâneos
      if (this.typingInterval) {
        clearInterval(this.typingInterval)
        this.typingInterval = null
      }

      this.typedText = ''

      this.typingInterval = setInterval(() => {
        this.typedText += fullText[i]
        i++
        if (i >= fullText.length) {
          clearInterval(this.typingInterval)
          this.typingInterval = null
          this.showInput = true
          this.updateCharCount()
        }
      }, this.typingSpeed)
    },
    async finalizarConfig() {
      const userData = JSON.parse(localStorage.getItem('userData'))

      const dadosBot = {
        nome: this.form.botName,
        storeId: this.form.storeId,
        personaId: this.form.languageStyle,
        accountId: userData.id,
      }

      const roles = [
        { description: this.form.exchangePolicy, role: 1 },
        { description: this.form.deliveryDelay, role: 2 },
        { description: this.form.cancelPolicy, role: 3 },
      ]

      const knowledge = [
        { titulo: '', conteudo: this.form.additionalInfo },
      ]

      const uuid = uuidv4()

      try {
        // Cria o assistente
        await createBot(uuid, dadosBot)
        this.$bvToast.toast('Assistente criado! Ensinando as políticas estabelecidas ...', {
          title: 'Assistente criado!',
          variant: 'success',
          solid: true,
        })

        setTimeout(async () => {
          // Salva as políticas, somente após o assistente ter sido criado
          await editRoleBot(uuid, roles)
          this.$bvToast.toast('O assistente já está ciente das políticas! Ensinando o conhecimento inserido...', {
            title: 'Políticas Salvas!',
            variant: 'success',
            solid: true,
          })

          // Salva o conhecimento
          await saveKnowledge(knowledge, uuid)
          this.$bvToast.toast('O assistente aprendeu o conteúdo informado!', {
            title: 'Conhecimento Salvo!',
            variant: 'success',
            solid: true,
          })

          // Alerta final com redirecionamento
          this.$swal({
            title: 'Seu assistente foi criado!',
            text: 'A seguir, selecione o assistente criado para inserir mais informações e testar.',
            icon: 'success',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          }).then(() => {
            this.$router.push('/bot') // ou passe uuid aqui se necessário
          })
        }, 5000)
      } catch (err) {
        console.error('Erro ao criar/configurar assistente:', err)
        this.$swal({
          title: 'Opa, algo deu errado!',
          text: 'Por favor, entre em contato com suporte.',
          icon: 'error',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
      }
    },

  },
}

</script>

<style>
.bot-onboarding {
  min-height: 100vh;
  background-color: #ffffff;
  max-width: 600px;
  margin: 0 auto;
}

.step-wrapper {
  width: 100%;
}

.bot-question {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  min-height: 56px;
  height: 56px;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.answer-input {
  width: 100%;
  border: none;
  border-bottom: 2px solid #ccc;
  outline: none;
  font-size: 1rem;
  padding: 8px 4px;
  resize: none;
  transition: border-color 0.3s ease;
  background-color: #fff;
}

.answer-input:focus {
  border-color: #7367f0;
}

.answer-input:disabled {
  background-color: #f9f9f9;
  cursor: not-allowed;
}

.char-counter {
  font-size: 0.75rem;
  color: #999;
  position: absolute;
  right: 0;
  bottom: -20px;
}

.navigation-buttons {
  max-width: 600px;
}

/* Dots "..." thinking animation */
.thinking-dots {
  display: inline-block;
  font-size: 1.5rem;
  letter-spacing: 4px;
  animation: blinkDots 1s infinite steps(1);
}

@keyframes blinkDots {
  0% { opacity: 0.2 }
  50% { opacity: 1 }
  100% { opacity: 0.2 }
}

.language-cards-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.language-card {
  cursor: pointer;
  border: 2px solid #ccc;
  border-radius: 12px;
  padding: 1rem;
  width: calc(50% - 0.5rem); /* 2 cards por linha com gap */
  background-color: #fff;
  transition: all 0.3s ease;
  text-align: left;
  min-width: 240px;
}

.language-card:hover {
  border-color: #7367f0;
}

.language-card.selected {
  border-color: #7367f0;
  background-color: #f4f3ff;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.85);
  z-index: 10;
  font-size: 1.2rem;
  font-weight: 500;
}

</style>
